import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  PlanningBalancesDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import AddShiftSidepanel from 'modules/shift_management/components/ShiftManagement/AddShiftSidepanel'
import AssignTemplateSidepanel from 'modules/shift_management/components/ShiftManagement/AssignTemplateSidepanel'
import NoTemplatesFoundModal from 'modules/shift_management/components/ShiftManagement/NoTemplatesFoundModal'
import OverlappingShiftsModal from 'modules/shift_management/components/ShiftManagement/OverlappingShiftsModal'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useAddRestDays } from 'modules/shift_management/hooks/useAddRestDays'
import type { Props as AssignTemplateAttributes } from 'modules/shift_management/hooks/useAssignTemplate'
import { countCellsSelected } from 'modules/shift_management/hooks/useCountCellsSelected'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import useRefreshLeaves from 'modules/shift_management/hooks/useRefreshLeaves/useRefreshLeaves'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { AddShiftOptionType } from 'modules/shift_management/lib/addShiftOptions'
import buildAddRestDaysParameters from 'modules/shift_management/lib/buildAddRestDaysParameters'
import {
  trackAddItemToShiftScheduleStart,
  trackAddShiftStartEvent,
  trackAddTimeOffFromShiftStartEvent,
} from 'modules/shift_management/lib/trackEvents'
import { ConflictsProps } from 'modules/shift_management/lib/types'
import shiftTemplates from 'modules/shift_management/store/api/collections/shiftTemplates'

import AddLeaveModal from './AddLeaveModal'

export interface Props {
  disabled?: boolean
  hasTimeOffModule: boolean
}

const AddShiftButton = ({ disabled, hasTimeOffModule }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const [{ shiftsRows }] = useShiftsRows()
  const { daysByRow, isEnabled: hasSelection, shifts, restDays } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()

  const restDaysParams = useUnsafeMemo(() => {
    return buildAddRestDaysParameters(daysByRow, view)
  }, [daysByRow, view])

  const { onSubmit: onAddRestDay } = useAddRestDays({
    params: restDaysParams,
    onSuccess: () => {
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })
    },
  })

  const [openConflictModal, setOpenConflictModal] = React.useState<boolean>(false)
  const [isAddShiftSidepanelOpen, setIsAddShiftSidepanelOpen] = React.useState<boolean>(false)
  const [isTemplateSidepanelOpen, setIsTemplateSidepanelOpen] = React.useState<boolean>(false)
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = React.useState<boolean>(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | null>(null)
  const [startDate, setStartDate] = React.useState<string>('')
  const [endDate, setEndDate] = React.useState<string>('')
  const apolloClient = useApolloClient()

  const { loading } = useAsyncData(async () => {
    await shiftTemplates.fetch()
  }, [])

  const [conflicts, setConflicts] = React.useState<ConflictsProps>([])

  const [params, setParams] = React.useState<AssignTemplateAttributes['params'] | null>(null)

  const isButtonDisabled = disabled || loading
  const isDailyWithSelection = hasSelection && view.isDaily
  const canAddTemplate = !hasSelection || (view.isByEmployees && !isDailyWithSelection)
  const activeTemplates = shiftTemplates.active
  const isRestDaysDisabled =
    !hasFeatureFlag('shift_management_rest_day') || view.isByAreas || !hasSelection
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')

  const selectedEmployeesIds = Object.keys(daysByRow)
  const impactedEmployees = selectedEmployeesIds.length

  // Has permission to add leaves to at least one employee, so we show the button
  const hasPermissionsToCreateLeaves = shiftsRows.some((shiftsRow) => shiftsRow.canCreateLeaves)

  const canAddLeaves = hasTimeOffModule && view.isByEmployees && hasPermissionsToCreateLeaves

  // We will not allow creating leaves for multiple employees
  // So here we find the one selected employee to check permissions
  const selectedEmployeeShiftRow = shiftsRows.find(
    (row) => row.id === Number(selectedEmployeesIds[0])
  )
  const canAddLeavesToSelected = selectedEmployeeShiftRow?.canCreateLeaves ?? false

  const handleAddLeave = () => {
    // Getting the first employee dates as button
    // is only available if we only select one employee
    const selectedEmployeeDates = Object.values(daysByRow)
    const selectedDates = selectedEmployeeDates[0] ?? []
    const sortedDates =
      selectedDates.length > 0
        ? [selectedDates[0], selectedDates[selectedDates.length - 1]]
            .map((date) => moment(date))
            .sort((a, b) => a.diff(b))
            .map((date) => dateToString(date, 'backend'))
        : ['', '']

    const [firstDate, lastDate] = sortedDates
    setStartDate(firstDate || '')
    setEndDate(lastDate || '')

    const employeeId = selectedEmployeeShiftRow?.id
    setSelectedEmployeeId(employeeId || null)

    setIsAddLeaveModalOpen(true)
    trackAddTimeOffFromShiftStartEvent({ view })
  }

  // Button is visible but disabled in these cases
  const isTimeOffOptionDisabled = !hasSelection || impactedEmployees > 1 || !canAddLeavesToSelected

  const trackAddShiftStart = (shiftType: AddShiftOptionType) => {
    const cellCount = countCellsSelected({ daysByRow, shifts, restDays, view })
    trackAddShiftStartEvent({
      view,
      impactedEmployees,
      cellCount,
      shiftType,
      from: 'top_bar',
    })
  }

  const onOpenMenu = () => {
    if (isButtonDisabled) return

    const cellCount = countCellsSelected({ daysByRow, shifts, restDays, view })
    trackAddItemToShiftScheduleStart({ impactedEmployees, cellCount, view, from: 'top_bar' })
  }

  const basicOption: Option[] = [
    {
      onClick: () => {
        setIsAddShiftSidepanelOpen(true), trackAddShiftStart('savedCustom')
      },
      disabled: false,
      title: i18n.t('shift_management.actions.add_shift'),
    },
  ]

  const addRestDayOptions: Option[] = [
    {
      onClick: onAddRestDay,
      description: isRestDaysDisabled
        ? i18n.t('shift_management.actions.add_rest_day_tooltip')
        : undefined,
      disabled: isRestDaysDisabled,
      title: i18n.t('shift_management.actions.add_rest_day'),
    },
  ]

  const addTemplateOptions: Option[] = [
    {
      onClick: () => {
        setIsTemplateSidepanelOpen(true)
        trackAddShiftStart('template')
      },
      disabled: false,
      title: i18n.t('shift_management.actions.add_rotating_shift'),
    },
  ]

  const addLeaveOptions: Option[] = [
    {
      onClick: handleAddLeave,
      description: isTimeOffOptionDisabled
        ? i18n.t('shift_management.actions.add_time_off_disabled')
        : undefined,
      disabled: isTimeOffOptionDisabled,
      title: i18n.t('shift_management.actions.add_time_off'),
    },
  ]
  const { refreshLeaves } = useRefreshLeaves()

  const onCloseLeaveModal = async () => {
    setIsAddLeaveModalOpen(false)
    dispatchSelection({
      type: 'CLEAR_SELECTION',
    })

    if (selectedEmployeeShiftRow) {
      await refreshLeaves([selectedEmployeeShiftRow])
    }
    // TODO: Actually update the cache properly
    apolloClient.refetchQueries({
      include: [ShiftManagementMainPageDocument, PlanningBalancesDocument],
    })
    selectedEmployeeShiftRow && refreshStats([selectedEmployeeShiftRow])
  }

  const options = (): Option[] => {
    const optionsList = []

    optionsList.push(...basicOption)

    if (canAddTemplate) {
      optionsList.push(...addTemplateOptions)
    }

    if (view.isByEmployees) {
      optionsList.push(...addRestDayOptions)
    }

    if (canAddLeaves) {
      optionsList.push(...addLeaveOptions)
    }

    return optionsList
  }

  const onCloseAddShiftSidepanel = () => {
    setIsAddShiftSidepanelOpen(false)
  }

  return (
    <>
      {isAddShiftSidepanelOpen && <AddShiftSidepanel onClose={onCloseAddShiftSidepanel} />}

      {isTemplateSidepanelOpen && !loading && (
        <>
          {!!shiftTemplates.active.length ? (
            <AssignTemplateSidepanel
              shiftTemplates={activeTemplates}
              onClose={() => setIsTemplateSidepanelOpen(false)}
              onOpenConflictModal={() => setOpenConflictModal(true)}
              onSetConflicts={setConflicts}
              onSetParams={setParams}
            />
          ) : (
            <NoTemplatesFoundModal onClose={() => setIsTemplateSidepanelOpen(false)} />
          )}
        </>
      )}
      {openConflictModal && params && (
        <OverlappingShiftsModal
          onClose={() => setOpenConflictModal(false)}
          conflicts={conflicts}
          params={{
            ...params,
            startDate: dateToString(params.startDate, 'backend'),
            endDate: dateToString(params.endDate, 'backend'),
          }}
        />
      )}

      {isAddLeaveModalOpen && selectedEmployeeId && (
        <AddLeaveModal
          currentEmployeeId={selectedEmployeeId}
          onClose={onCloseLeaveModal}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      <Box>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            {newEntryFlowEnabled ? (
              <F0Button
                hideLabel
                icon={Add}
                onClick={onOpenMenu}
                size="md"
                variant="outline"
                disabled={isButtonDisabled}
              />
            ) : (
              <Button
                type="secondary"
                onClick={onOpenMenu}
                disabled={isButtonDisabled}
                iconName="add"
              />
            )}
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {options().map((option, index) => (
              <PopoverMenu.TouchableOption
                key={index}
                disabled={option.disabled}
                title={option.title}
                description={option.description}
                onClick={option.onClick}
              />
            ))}
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
    </>
  )
}

export default observer(AddShiftButton)
